import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { showToast } from 'stores/ToastStore';
import Spinner from 'components/ui/Spinner';
import Pagination from 'components/v2/PaginationV2';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import Table from 'components/v2/Table';
import AddButton from 'components/v2/buttons/AddButton';
import Checkbox from 'components/v2/forms/Checkbox';
import CheckIcon from 'components/v2/icons/CheckIcon';
import AccessorialDetails from 'components/modules/user/shipper/AccessorialDetails';
import {
  getAccessorialRequestRule,
  removeAccessorialRequestRule,
} from 'utils/UserServiceAPIUtils';
import SessionStore from 'stores/SessionStore';
import AccessorialAddEditModal from 'components/modules/user/shipper/AccessorialAddEditModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';

const ITEMS_PER_PAGE_LIMIT = 20;

const DETAIL_MODES = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
};

const TABLE_ACTIONS = {
  REMOVE: 'remove',
};

export default function AccessorialsList({ id, className }) {
  const { t } = useTranslation();

  function renderSpinner() {
    return <Spinner showMessage message={t('LOADING')} />;
  }

  const [selectedAccessorial, setSelectedAccessorial] = useState();
  const [detailMode, setDetailMode] = useState();
  const [refresh, setRefresh] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [selectedAccessorials, setSelectedAccessorials] = useState([]);
  const [userAccessorialData, setUserAccessorialData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tableAction, setTableAction] = useState('');
  const [accessorialsToRemove, setAccessorialsToRemove] = useState([]);
  const [confirmModalDetails, setConfirmModalDetails] = useState({});
  const canEditSettings = SessionStore.hasShipperOnboardingRole();
  const toggleAllRef = useRef();
  async function fetchUserAccessorials(id) {
    try {
      const userResponse = await getAccessorialRequestRule(id, {
        count: true,
        sort: 'ASC',
        page: activePage,
        per_page: ITEMS_PER_PAGE_LIMIT,
      });

      if (!userResponse) {
        throw new Error('No response from API');
      }

      const accessorialRules =
        userResponse?.data?.getAccessorialRequestRule?.items ?? [];

      const userResponseTotalCount =
        userResponse?.data?.getAccessorialRequestRule?.totalCount;

      const userResponseTotalPages =
        userResponse?.data?.getAccessorialRequestRule?.totalPages;

      setUserAccessorialData(
        accessorialRules?.map((accessorialRule) => {
          return {
            id: accessorialRule.accessorial,
            ...accessorialRule,
          };
        }),
      );

      setTotalCount(userResponseTotalCount ?? accessorialRules.length);
      setTotalPages(userResponseTotalPages ?? 1);
    } catch (err) {
      showToast(t('ERROR_FETCHING_DATA'), null, 'error');
      console.error('Error during fetchUserAccessorials: ', err.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetchUserAccessorials(id);
    }
  }, [activePage, refresh, id]);

  useEffect(() => {
    if (accessorialsToRemove?.length) {
      setConfirmModalDetails({
        show: true,
        title: t('REMOVE_ACCESSORIAL'),
        message: t('REMOVE_ACCESSORIAL_MESSAGE'),
        onHide: handleRemoveAccessorialsCancel,
        onConfirm: removeAccessorials,
      });
    }
  }, [accessorialsToRemove]);

  function renderFooter() {
    return (
      <tr>
        <td colSpan="5">
          <Pagination
            perPage={ITEMS_PER_PAGE_LIMIT}
            page={activePage}
            total={totalCount}
            pages={totalPages}
            showCount
            onPageClick={(eventKey) => handlePageChange(eventKey)}
          />
        </td>
      </tr>
    );
  }

  function handleToggleAllCheck(checked) {
    if (checked) {
      setSelectedAccessorials(userAccessorialData.map((row) => row.id));
    } else {
      setSelectedAccessorials([]);
    }
    if (toggleAllRef?.current?.indeterminate) {
      toggleAllRef.current.indeterminate = false;
    }
  }

  function isIndeterminate(selected) {
    const selectedCount = selected.length;
    return selectedCount > 0 && userAccessorialData?.length !== selectedCount;
  }

  function handleToggleCheck(id) {
    return function(checked) {
      let updatedSelections;
      if (checked) {
        updatedSelections = [...selectedAccessorials, id];
        setSelectedAccessorials(updatedSelections);
      } else {
        updatedSelections = selectedAccessorials.filter((item) => item !== id);
        setSelectedAccessorials(updatedSelections);
      }

      if (toggleAllRef?.current?.indeterminate) {
        toggleAllRef.current.indeterminate = isIndeterminate(updatedSelections);
      }
    };
  }

  function handlePageChange(eventKey) {
    setActivePage(eventKey);
  }

  function handleHideModal() {
    setSelectedAccessorial(null);
    setDetailMode(null);
  }

  function handleViewAccessorial(accessorial) {
    return function(e) {
      e.preventDefault();
      setSelectedAccessorial(accessorial);
      setDetailMode(DETAIL_MODES.VIEW);
    };
  }

  function handleEditAccessorial(accessorial) {
    if (!canEditSettings) return;
    setSelectedAccessorial(accessorial);
    setDetailMode(DETAIL_MODES.EDIT);
  }

  function handleAddOrEditSaved(accessorial) {
    if (!canEditSettings) return;
    setRefresh(accessorial);
  }

  function handleAdd() {
    setDetailMode(DETAIL_MODES.ADD);
  }

  function isViewMode() {
    return detailMode === DETAIL_MODES.VIEW;
  }

  function isEditMode() {
    return detailMode === DETAIL_MODES.EDIT;
  }

  function isAddMode() {
    return detailMode === DETAIL_MODES.ADD;
  }

  function handleAccessorialTableSelect(e) {
    const action = e?.target?.value;
    if (action === TABLE_ACTIONS.REMOVE) {
      setTableAction(action);
      handleRemoveBulkAccessorials();
    }
  }

  function resetConfirmModal() {
    setConfirmModalDetails({
      show: false,
      title: '',
      message: '',
      onHide: null,
      onConfirm: null,
      affirmative: null,
    });
  }

  async function removeAccessorials() {
    try {
      if (accessorialsToRemove.length) {
        setConfirmModalDetails((modalDetails) => {
          return {
            ...modalDetails,
            show: false,
          };
        });
        await removeAccessorialRequestRule(id, accessorialsToRemove);
        handleToggleAllCheck(false);
        setTableAction('');
        setRefresh(accessorialsToRemove);
        showToast(t('REMOVE_SSR_ACCESSORIALS_SUCCESS'));
      }
    } catch (err) {
      console.error('Error during removeAccessorials: ', err.message);
      showToast(t('REMOVE_SSR_ACCESSORIALS_FAILURE'), null, 'error');
    }
  }

  function handleRemoveSingleAccessorial({ accessorial }) {
    setAccessorialsToRemove([accessorial]);
  }

  function handleRemoveBulkAccessorials() {
    setAccessorialsToRemove(selectedAccessorials);
  }

  function handleRemoveAccessorialsCancel() {
    handleToggleAllCheck(false);
    setTableAction('');
    setAccessorialsToRemove([]);
    resetConfirmModal();
  }

  const columns = [
    {
      field: 'id',
      label: '',
      renderHeader: () => (
        <Checkbox
          id="id-renderHeader"
          checked={selectedAccessorials?.length === userAccessorialData?.length}
          ref={toggleAllRef}
          onChange={handleToggleAllCheck}
          disabled={!canEditSettings}
        />
      ),
      render: (value) => {
        return (
          <Checkbox
            id="id-render"
            checked={selectedAccessorials.includes(value)}
            onChange={handleToggleCheck(value)}
            disabled={!canEditSettings}
          />
        );
      },
    },
    {
      field: 'accessorial',
      label: t('ACCESSORIAL'),
      render: (value, row) => {
        return (
          <a href="#" onClick={handleViewAccessorial(row)}>
            {t(value.toUpperCase())}
          </a>
        );
      },
    },
    {
      field: 'rules',
      label: t('DOCUMENTS'),
      render: (documents) => {
        return documents?.length || 0;
      },
    },
    {
      field: 'customer_approval_required',
      label: t('CUSTOMER_APPROVAL_REQUIRED'),
      render: (value, row, index) => {
        if (index < 5 && value) {
          return (
            <div className="">
              <CheckIcon className="text-green-500 h-5 w-5" />
            </div>
          );
        }
        return null;
      },
    },
    {
      field: 'action',
      label: '',
      render: (_, row, i) => {
        return (
          <div className="flex justify-end">
            <Dropdown disabled={!canEditSettings} useIcon>
              <Dropdown.Button onClick={() => handleEditAccessorial(row)}>
                {t('EDIT')}
              </Dropdown.Button>
              <Dropdown.Button
                onClick={() => handleRemoveSingleAccessorial(row)}
              >
                {titleize(t('REMOVE'))}
              </Dropdown.Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];
  return isLoading ? (
    renderSpinner()
  ) : (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2 items-center">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            {t('INVOICEABLE_ACCESSORIAL')}
          </h3>
          <AddButton disabled= {!canEditSettings} size="small" onClick={handleAdd} />
        </div>
        <select
          value={tableAction}
          onChange={handleAccessorialTableSelect}
          className="form-select"
        >
          <option value="">{titleize(t('SELECT_ACTION'))}</option>
          <option value="remove" disabled={!selectedAccessorials.length || !canEditSettings}>
            {titleize(t('REMOVE'))}
          </option>
        </select>
      </div>
      <Table
        columns={columns}
        footer={renderFooter}
        data={userAccessorialData}
        headers={columns}
        className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
      />
      <AccessorialDetails
        accessorial={selectedAccessorial}
        onHide={handleHideModal}
        viewMode={isViewMode()}
      />
      <AccessorialAddEditModal
        shipperId={id}
        accessorial={selectedAccessorial}
        editMode={isEditMode()}
        addMode={isAddMode()}
        onHide={handleHideModal}
        onSave={handleAddOrEditSaved}
      />
      {confirmModalDetails?.show && (
        <ConfirmModal
          show={confirmModalDetails?.show}
          title={confirmModalDetails?.title}
          message={confirmModalDetails?.message}
          onHide={confirmModalDetails?.onHide}
          onConfirm={confirmModalDetails?.onConfirm}
          affirmative={confirmModalDetails?.affirmative}
        />
      )}
    </>
  );
}
